/**
 * storage.ts
 * Provides a unified localStorage facade with TTL support and namespacing.
 * All keys are prefixed with 'societea:' to avoid collisions.
 */

const NS = "societea:";

/**
 * Wrapper type for values stored with TTL
 */
type WithTTL<T> = { 
  v: T;    // value
  e: number; // expiration timestamp
};

/**
 * Helper to namespace keys
 */
const k = (key: string) => `${NS}${key}`;

/**
 * Storage facade for localStorage with JSON serialization and TTL support
 */
export const storage = {
  /**
   * Get a JSON value from localStorage
   * @param key - The key to retrieve (will be namespaced automatically)
   * @param fallback - Value to return if key doesn't exist or parsing fails
   * @returns The parsed value or fallback
   */
  getJSON<T>(key: string, fallback: T): T {
    try {
      const raw = localStorage.getItem(k(key));
      return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
      return fallback;
    }
  },

  /**
   * Set a JSON value in localStorage
   * @param key - The key to store (will be namespaced automatically)
   * @param value - The value to store (will be JSON stringified)
   */
  setJSON(key: string, value: unknown): void {
    try {
      localStorage.setItem(k(key), JSON.stringify(value));
    } catch {
      // localStorage may be full or disabled; fail silently
    }
  },

  /**
   * Remove a key from localStorage
   * @param key - The key to remove (will be namespaced automatically)
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(k(key));
    } catch {
      // fail silently
    }
  },

  /**
   * Set a JSON value with TTL (time-to-live)
   * @param key - The key to store
   * @param value - The value to store
   * @param ttlMs - Time-to-live in milliseconds
   */
  setJSONWithTTL(key: string, value: unknown, ttlMs: number): void {
    this.setJSON(key, { v: value, e: Date.now() + ttlMs } satisfies WithTTL<unknown>);
  },

  /**
   * Get a JSON value with TTL, automatically removing expired values
   * @param key - The key to retrieve
   * @param fallback - Value to return if key doesn't exist, is expired, or parsing fails
   * @returns The parsed value or fallback
   */
  getJSONWithTTL<T>(key: string, fallback: T): T {
    const payload = this.getJSON<WithTTL<T> | null>(key, null);
    if (!payload || Date.now() > payload.e) {
      this.remove(key);
      return fallback;
    }
    return payload.v;
  },
};

